import React, { useState } from "react"

import {
  Autocomplete,
  Checkbox,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { ReactComponent as IconSearch } from "assets/common/svg/search.svg"
import { useTranslation } from "react-i18next"
import { FixedSizeList as List, ListChildComponentProps } from "react-window"

import { StudentProps } from "../SchoolTransactions.types"

interface TextWithTooltipProps {
  text: string
  children: React.ReactElement
}

const TextWithTooltip = ({ text, children }: TextWithTooltipProps) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false)
  const textRef = React.useRef<HTMLElement | null>(null)

  const checkOverflow = React.useCallback(() => {
    if (textRef.current) {
      setIsOverflowing(
        textRef.current.scrollWidth > textRef.current.clientWidth,
      )
    }
  }, [])

  React.useEffect(() => {
    checkOverflow()
    const timeoutId = setTimeout(checkOverflow, 0)

    let resizeObserver: ResizeObserver | null = null
    if (textRef.current) {
      resizeObserver = new ResizeObserver(checkOverflow)
      resizeObserver.observe(textRef.current)
    }

    return () => {
      clearTimeout(timeoutId)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [checkOverflow, text])

  const handleRef = React.useCallback(
    (node: HTMLElement | null) => {
      textRef.current = node
      if (node) {
        checkOverflow()
      }
    },
    [checkOverflow],
  )

  const clonedElement = React.cloneElement(children, {
    ref: handleRef,
  })

  if (isOverflowing) {
    return (
      <Tooltip title={text} arrow>
        {clonedElement}
      </Tooltip>
    )
  }

  return clonedElement
}

type VirtualizedListBoxProps = {
  options: StudentProps[]
  selectedOptions: StudentProps[]
  handleSelected: (option: StudentProps, scrollOffset?: number) => void
  savedScrollOffset?: number
  listHeight?: number
} & React.HTMLAttributes<HTMLDivElement>

const VirtualizedListBox = React.forwardRef<
  HTMLDivElement,
  VirtualizedListBoxProps
>(function VirtualizedListBox(
  {
    options,
    selectedOptions,
    handleSelected,
    savedScrollOffset = 0,
    listHeight = 300,
    ...other
  }: VirtualizedListBoxProps,
  ref,
) {
  // Filter ownerState and other MUI-specific props that should not be passed to DOM
  const { ownerState: _ownerState, ...domProps } = other as Record<
    string,
    unknown
  >
  const scrollOffsetRef = React.useRef<number>(0)

  const handleScroll = ({ scrollOffset }: { scrollOffset: number }) => {
    scrollOffsetRef.current = scrollOffset
  }

  const handleOptionClick = (
    event: React.MouseEvent | React.ChangeEvent,
    option: StudentProps,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    handleSelected(option, scrollOffsetRef.current)
  }

  const renderRow = React.useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const option = options[index]
      const isSelected = selectedOptions.some((item) => item.id === option.id)

      return (
        <div key={option.id} style={style}>
          <li
            onClick={(event) => {
              event.preventDefault()
              handleOptionClick(event, option)
            }}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "4px 8px",
              cursor: "pointer",
              listStyle: "none",
              width: "100%",
            }}
          >
            <Checkbox
              checked={isSelected}
              onChange={(event) => {
                event.preventDefault()
                handleOptionClick(event, option)
              }}
            />
            <Stack
              direction="row"
              columnGap={1}
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <TextWithTooltip
                text={`${option.first_name} ${option.last_name}`}
              >
                <Typography
                  fontWeight="500"
                  color="mockup.neutral10"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {option.first_name} {option.last_name}
                </Typography>
              </TextWithTooltip>
              <Typography
                fontWeight="500"
                color="mockup.neutral40"
                sx={{
                  flexShrink: 0,
                }}
              >
                ({option.login})
              </Typography>
            </Stack>
          </li>
        </div>
      )
    },
    [options, selectedOptions, handleSelected],
  )

  const initialScrollOffset =
    savedScrollOffset > 0 ? savedScrollOffset : undefined
  const listKey = `list-${savedScrollOffset}`

  return (
    <div ref={ref} {...domProps}>
      <List
        key={listKey}
        height={listHeight}
        width="100%"
        itemSize={40}
        itemCount={options.length}
        onScroll={handleScroll}
        initialScrollOffset={initialScrollOffset}
      >
        {renderRow}
      </List>
    </div>
  )
})

interface AutocompleteFieldProps {
  selectedOptions: StudentProps[]
  handleSelected: (options: StudentProps[]) => void
  options: StudentProps[]
  disabled?: boolean
}

const AutocompleteField = ({
  selectedOptions,
  handleSelected,
  options,
  disabled = false,
}: AutocompleteFieldProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [filteredOptions, setFilteredOptions] = React.useState(options)
  const [isFocus, setIsFocus] = useState(false)
  const [localSelectedOptions, setLocalSelectedOptions] =
    React.useState<StudentProps[]>(selectedOptions)
  const [savedScrollOffset, setSavedScrollOffset] = React.useState<number>(0)

  React.useEffect(() => {
    setLocalSelectedOptions(selectedOptions)
  }, [selectedOptions])

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
  ) => {
    const lowercasedValue = value.trim().toLowerCase()
    setFilteredOptions(
      options.filter((option) =>
        `${option.first_name} ${option.last_name}`
          .toLowerCase()
          .includes(lowercasedValue),
      ),
    )
  }

  React.useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  const handleLocalSelected = React.useCallback(
    (option: StudentProps, scrollOffset?: number) => {
      if (scrollOffset !== undefined) {
        setSavedScrollOffset(scrollOffset)
      }
      setLocalSelectedOptions((prev) => {
        const isSelected = prev.some((item) => item.id === option.id)
        if (isSelected) {
          return prev.filter((item) => item.id !== option.id)
        } else {
          return [...prev, option]
        }
      })
    },
    [],
  )

  const handleClose = (event: React.SyntheticEvent, reason: string) => {
    if (reason === "selectOption") {
      return
    }
    setIsFocus(false)
    setSavedScrollOffset(0)
    handleSelected(localSelectedOptions)
  }

  const ITEM_SIZE = 40
  const MAX_HEIGHT = 300
  const calculatedHeight = Math.min(
    filteredOptions.length * ITEM_SIZE,
    MAX_HEIGHT,
  )
  const listHeight = filteredOptions.length > 0 ? calculatedHeight : 0

  return (
    <Autocomplete<StudentProps, false>
      disabled={disabled}
      disableCloseOnSelect
      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={handleInputChange}
      onClose={handleClose}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          "& .MuiAutocomplete-input": {
            minWidth: 0,
            flex: "1 1 auto",
            width: "100% !important",
          },
        },
      }}
      slots={{
        listbox: React.useMemo(
          () =>
            React.forwardRef<
              HTMLDivElement,
              React.HTMLAttributes<HTMLDivElement>
            >(function VirtualizedListBoxComp(listboxprops, ref) {
              return (
                <VirtualizedListBox
                  selectedOptions={localSelectedOptions}
                  handleSelected={handleLocalSelected}
                  savedScrollOffset={savedScrollOffset}
                  listHeight={listHeight}
                  ref={ref}
                  {...listboxprops}
                  options={filteredOptions}
                />
              )
            }),
          [
            localSelectedOptions,
            filteredOptions,
            handleLocalSelected,
            savedScrollOffset,
            listHeight,
          ],
        ),
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
            variant="outlined"
            label={t("adminDashboard.searchForStudents")}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch
                    style={{ color: theme.palette.mockup.primary60 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {localSelectedOptions.length > 0 && (
                    <Chip
                      label={localSelectedOptions.length}
                      size="small"
                      color="primary"
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": {
                transform: "translate(40px, 16px) scale(1)",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
            InputLabelProps={{
              shrink: isFocus,
            }}
          />
        )
      }}
      options={filteredOptions}
    />
  )
}
export default AutocompleteField
